import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { fontPayload, itemArtworks, itemFontUploads, type Artwork } from '@/lib/artwork'
import type { Order, OrderItem } from '@/types'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import ArtworkPicker from '@/components/order/ArtworkPicker'
import { useToast } from '@/components/ui/toast'
import { invalidateOrderViews } from '@/components/order/lifecycle'

/** The item's stored files as the picker's uploads. Only files with a key can be kept. */
function storedFiles(item: OrderItem): Artwork[] {
  return itemArtworks(item)
    .filter((file) => file.key && file.path)
    .map((file) => ({
      artwork_name: file.name ?? 'artwork',
      artwork_path: file.path!,
      artwork_key: file.key!,
      artwork_size: file.size ?? 0,
      artwork_mime: file.mime ?? '',
    }))
}

/**
 * Add, remove or replace one item's artwork after the order was placed
 * (client request 2026-10-05: an order went in before the shirt picture was
 * uploaded). Saves the whole ordered list; the server applies the same rule
 * as Edit details, and the job sheet prints whatever is saved. A sheet
 * already printed has to be printed again.
 *
 * kind="font" does the same for the font behind the names and numbers
 * (client request 2026-10-14), when a customer sent the wrong one or the
 * team got it over WhatsApp.
 */
export default function EditArtworkModal({
  order,
  item,
  basePath,
  kind = 'artwork',
  onClose,
}: {
  order: Order
  item: OrderItem | null
  basePath: '/admin' | '/sales'
  kind?: 'artwork' | 'font'
  onClose: () => void
}) {
  const font = kind === 'font'
  const toast = useToast()
  const queryClient = useQueryClient()
  const [files, setFiles] = useState<Artwork[]>([])

  useEffect(() => {
    if (item) setFiles(font ? itemFontUploads(item) : storedFiles(item))
  }, [item, font])

  const save = useMutation({
    mutationFn: async () =>
      (
        await api.put<{ message?: string }>(
          `${basePath}/orders/${order.id}/items/${item!.id}/${font ? 'fonts' : 'artworks'}`,
          font
            ? fontPayload(files)
            : { artworks: files.map((file) => ({ artwork_name: file.artwork_name, artwork_key: file.artwork_key })) },
        )
      ).data,
    onSuccess: (res) => {
      invalidateOrderViews(queryClient)
      onClose()
      toast.success(res.message ?? (font ? 'Font updated' : 'Artwork updated'))
    },
    onError: (err) => toast.error(font ? 'Could not save the font' : 'Could not save the artwork', errorMessage(err)),
  })

  return (
    <Modal
      open={Boolean(item)}
      onClose={onClose}
      title={`${font ? 'Font file' : 'Artwork'} - ${item?.product ?? ''}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            <Save className="size-4" /> {font ? 'Save font' : 'Save artwork'}
          </Button>
        </div>
      }
    >
      {item && (
        <div className="space-y-3">
          <ArtworkPicker kind={kind} variant="dropzone" preview={!font} value={files} basePath={basePath} onChange={setFiles} />
          <p className="text-xs text-foreground/60">
            {font
              ? 'The font the floor uses to lay out the names and numbers.'
              : 'The job sheet prints these in this order (front first). If the sheet was already printed, print it again.'}
          </p>
        </div>
      )}
    </Modal>
  )
}
